import { type RefObject, useEffect, useState } from 'react';

import { useReducedMotion } from '../presence/useReducedMotion';

const PHASE_COUNT = 3;

/** Extra scroll (px) while content stays pinned — one beat per day (3 days). */
export const WALKTHROUGH_PIN_RUNWAY_PX = 150;

/** Day-3 hold — stay fixed after the last day beat without bleeding into the next section. */
export const WALKTHROUGH_PIN_HOLD_PX = 60;

/** Max runway scroll per wheel tick — small steps so each day gets several beats. */
export const WALKTHROUGH_RUNWAY_CAP_PX = 12;

export function getWalkthroughPinTrackScrollPx(): number {
  return WALKTHROUGH_PIN_RUNWAY_PX + WALKTHROUGH_PIN_HOLD_PX;
}

const WALKTHROUGH_FRAME_HEIGHT_MIN = 320;
const WALKTHROUGH_FRAME_HEIGHT_MAX = 960;

type WalkthroughLayoutCache = { frameH: number; trackH: number };

let walkthroughLayoutCache: WalkthroughLayoutCache | null = null;
let walkthroughExitedPastLockEnd = false;
let pinnedSessionStartScrollY: number | null = null;
let pinScrollLockSuspended = false;

export function resetWalkthroughPinSessionState(): void {
  walkthroughLayoutCache = null;
  walkthroughExitedPastLockEnd = false;
  pinnedSessionStartScrollY = null;
}

/**
 * Suspend the pin's wheel/touch speed clamp for a programmatic scroll (e.g. `scrollIntoView`
 * from a nav link or CTA). Without this, any scroll landing in the pin's lock zone — including
 * a smooth-scroll jump that only passes *through* it on the way to a section further down —
 * gets its per-event delta capped to `WALKTHROUGH_RUNWAY_CAP_PX`, which fights the browser's own
 * scroll animation and can stall it near where the pin engaged instead of reaching the target.
 * Call `resumeWalkthroughPinLock()` once the programmatic scroll settles.
 */
export function suspendWalkthroughPinLock(): void {
  pinScrollLockSuspended = true;
}

export function resumeWalkthroughPinLock(): void {
  pinScrollLockSuspended = false;
}

function cacheWalkthroughLayout(frameH: number): WalkthroughLayoutCache {
  const cached = { frameH, trackH: getWalkthroughPinTrackHeightPx(frameH) };
  walkthroughLayoutCache = cached;
  return cached;
}

export function restoreWalkthroughTrackHeight(track: HTMLElement): void {
  if (walkthroughLayoutCache) {
    track.style.height = `${walkthroughLayoutCache.trackH}px`;
  }
}

/** Measure content height without inflated pin-track feedback. */
export function measureWalkthroughFrameHeight(frame: HTMLElement, track: HTMLElement): number {
  if (walkthroughLayoutCache) {
    const quick = Math.ceil(frame.getBoundingClientRect().height);
    if (Math.abs(quick - walkthroughLayoutCache.frameH) < 48) {
      track.style.height = `${walkthroughLayoutCache.trackH}px`;
      return walkthroughLayoutCache.frameH;
    }
  }

  track.style.removeProperty('height');
  const measured = Math.ceil(frame.getBoundingClientRect().height);
  const frameH = Math.min(
    WALKTHROUGH_FRAME_HEIGHT_MAX,
    Math.max(WALKTHROUGH_FRAME_HEIGHT_MIN, measured)
  );
  cacheWalkthroughLayout(frameH);
  track.style.height = `${walkthroughLayoutCache!.trackH}px`;
  return frameH;
}

export function getWalkthroughPinTrackHeightPx(frameHeight: number): number {
  return frameHeight + getWalkthroughPinTrackScrollPx();
}

/** True while the walkthrough is pinned in the runway (not exited up or scrolled past). */
export function isWalkthroughPinEngaged(
  trackRect: Pick<DOMRect, 'top' | 'bottom'>,
  relative: number,
  stickyTop: number
): boolean {
  const lockEnd = getWalkthroughPinTrackScrollPx();
  if (relative < 0) {
    walkthroughExitedPastLockEnd = false;
    return false;
  }
  if (trackRect.bottom <= stickyTop + 64) {
    return false;
  }
  const rounded = Math.round(relative);
  if (walkthroughExitedPastLockEnd) {
    return false;
  }

  if (rounded <= lockEnd) return true;

  walkthroughExitedPastLockEnd = true;
  return false;
}

/** Cap in-runway scroll — small linear steps only (no step-boundary jumps). */
export function capWalkthroughScrollY(
  lastScrollY: number,
  scrollY: number,
  pinStartScrollY: number,
  lockEnd = getWalkthroughPinTrackScrollPx()
): number {
  const delta = scrollY - lastScrollY;
  if (delta === 0) return lastScrollY;

  const sign = Math.sign(delta);
  const target = lastScrollY + sign * Math.min(Math.abs(delta), WALKTHROUGH_RUNWAY_CAP_PX);
  return Math.max(pinStartScrollY, Math.min(pinStartScrollY + lockEnd, target));
}

export type WalkthroughSectionComputed = {
  showStage: boolean;
  pinned: boolean;
  activeDay: 1 | 2 | 3;
  chipsVisible: boolean;
  /** 0→1 progress through the full pin runway + hold, for UI that has more steps than days. */
  progressRatio: number;
};

export function getWalkthroughStickyTop(vh: number): number {
  return Math.min(Math.max(vh * 0.1, 64), 92);
}

export function getRunwayStepPx(runwayPx = WALKTHROUGH_PIN_RUNWAY_PX): number {
  return runwayPx / (PHASE_COUNT - 1);
}

export function runwayStepIndexFromRelative(
  relative: number,
  runwayPx = WALKTHROUGH_PIN_RUNWAY_PX
): number {
  const stepPx = getRunwayStepPx(runwayPx);
  if (relative < 0) return 0;
  if (relative > runwayPx) return PHASE_COUNT - 1;
  return Math.min(PHASE_COUNT - 1, Math.max(0, Math.floor(relative / stepPx)));
}

export function scrollYForRunwayStep(
  pinStartScrollY: number,
  stepIndex: number,
  runwayPx = WALKTHROUGH_PIN_RUNWAY_PX
): number {
  const stepPx = getRunwayStepPx(runwayPx);
  const clamped = Math.min(PHASE_COUNT - 1, Math.max(0, stepIndex));
  return pinStartScrollY + clamped * stepPx;
}

function stageFromStepIndex(
  stepIndex: number
): Pick<WalkthroughSectionComputed, 'activeDay' | 'chipsVisible'> {
  if (stepIndex <= 0) return { activeDay: 1, chipsVisible: true };
  if (stepIndex === 1) return { activeDay: 2, chipsVisible: true };
  return { activeDay: 3, chipsVisible: true };
}

/** Pure scroll math for a short sticky pin track. */
export function computeWalkthroughPinStage(
  trackRect: Pick<DOMRect, 'top' | 'bottom'>,
  _trackHeight: number,
  vh: number,
  stickyTop = getWalkthroughStickyTop(vh),
  runwayPx = WALKTHROUGH_PIN_RUNWAY_PX,
  relativeOverride?: number,
  scrollDirection: 'forward' | 'backward' | 'idle' = 'idle'
): WalkthroughSectionComputed {
  // Capture *before* isWalkthroughPinEngaged (or any early return) can clear the flag.
  const wasExited = walkthroughExitedPastLockEnd;
  const lockEnd = getWalkthroughPinTrackScrollPx();
  const progressRatioFor = (rel: number) => Math.min(1, Math.max(0, rel / lockEnd));

  if (vh <= 0 || trackRect.bottom < -300) {
    return { showStage: false, pinned: false, activeDay: 1, chipsVisible: false, progressRatio: 0 };
  }

  if (trackRect.top > vh) {
    // Section is below the viewport (user is above the section, or the section has scrolled
    // off the bottom during a backward pass). This is the safe moment to reset the
    // walkthroughExitedPastLockEnd flag — the Day 3→Day 1 swap is invisible.
    if (wasExited) {
      walkthroughExitedPastLockEnd = false;
    }
    return { showStage: false, pinned: false, activeDay: 1, chipsVisible: false, progressRatio: 0 };
  }

  const showStage = trackRect.top < vh * 0.98 && trackRect.bottom > 16;
  const relative = relativeOverride ?? stickyTop - trackRect.top;
  const pinned =
    trackRect.top <= stickyTop + 32 && isWalkthroughPinEngaged(trackRect, relative, stickyTop);

  if (!pinned) {
    if (wasExited) {
      if (scrollDirection === 'backward' && relative <= runwayPx) {
        return {
          showStage,
          pinned: false,
          ...stageFromStepIndex(runwayStepIndexFromRelative(relative, runwayPx)),
          progressRatio: progressRatioFor(relative),
        };
      }
      // User has completed the walkthrough. Keep showing Day 3 with the natural showStage
      // until the section scrolls off the viewport bottom (handled above). This prevents
      // the jarring Day 3→Day 1 swap that occurs when any backward scroll crosses relative=0.
      return { showStage, pinned: false, activeDay: 3, chipsVisible: true, progressRatio: 1 };
    }
    if (trackRect.bottom <= stickyTop + 64 || relative > 0) {
      return { showStage: true, pinned: false, activeDay: 3, chipsVisible: true, progressRatio: 1 };
    }
    return { showStage, pinned: false, activeDay: 1, chipsVisible: false, progressRatio: 0 };
  }

  const stepIndex = runwayStepIndexFromRelative(relative, runwayPx);
  const stage = stageFromStepIndex(stepIndex);

  return { showStage: true, pinned: true, ...stage, progressRatio: progressRatioFor(relative) };
}

export type WalkthroughSectionState = WalkthroughSectionComputed;

/**
 * Drives walkthrough phases while a compact sticky pin track is in view.
 */
export function useWalkthroughSectionStage(
  trackRef: RefObject<HTMLElement | null>
): WalkthroughSectionState {
  const reducedMotion = useReducedMotion();
  const [showStage, setShowStage] = useState(reducedMotion);
  const [pinned, setPinned] = useState(reducedMotion);
  const [activeDay, setActiveDay] = useState<1 | 2 | 3>(reducedMotion ? 3 : 1);
  const [chipsVisible, setChipsVisible] = useState(reducedMotion);
  const [progressRatio, setProgressRatio] = useState(reducedMotion ? 1 : 0);

  useEffect(() => {
    if (reducedMotion) {
      setShowStage(true);
      setPinned(false);
      setActiveDay(3);
      setChipsVisible(true);
      setProgressRatio(1);
      return;
    }

    const track = trackRef.current;
    if (!track) return;

    let rafId: number | null = null;
    let lastRelative: number | null = null;

    const update = () => {
      const el = trackRef.current;
      if (!el) return;

      const vh = window.innerHeight;
      const rect = el.getBoundingClientRect();
      const trackHeight = el.offsetHeight;
      if (trackHeight <= 0 || vh <= 0) return;

      const stickyTop = getWalkthroughStickyTop(vh);
      const metrics = readPinMetrics(el);
      const relative = metrics?.relative ?? stickyTop - rect.top;
      const scrollDirection =
        lastRelative == null
          ? 'idle'
          : relative > lastRelative
            ? 'forward'
            : relative < lastRelative
              ? 'backward'
              : 'idle';
      lastRelative = relative;
      const next = computeWalkthroughPinStage(
        rect,
        trackHeight,
        vh,
        stickyTop,
        WALKTHROUGH_PIN_RUNWAY_PX,
        relative,
        scrollDirection
      );
      setShowStage(next.showStage);
      setPinned(next.pinned);
      setActiveDay(next.activeDay);
      setChipsVisible(next.chipsVisible);
      setProgressRatio(next.progressRatio);
    };

    const schedule = () => {
      if (rafId != null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        update();
      });
    };

    update();
    window.addEventListener('scroll', schedule, { passive: true, capture: true });
    window.addEventListener('resize', schedule, { passive: true });
    window.addEventListener('pageshow', schedule, { passive: true });

    const ro = new ResizeObserver(schedule);
    ro.observe(track);

    return () => {
      if (rafId != null) cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('pageshow', schedule);
      ro.disconnect();
    };
  }, [reducedMotion, trackRef]);

  return { showStage, pinned, activeDay, chipsVisible, progressRatio };
}

type PinMetrics = {
  stickyTop: number;
  trackTop: number;
  trackBottom: number;
  pinStartScrollY: number;
  relative: number;
  runway: number;
};

function readPinMetrics(track: HTMLElement): PinMetrics | null {
  const vh = window.innerHeight;
  if (vh <= 0) return null;
  const stickyTop = getWalkthroughStickyTop(vh);
  const trackRect = track.getBoundingClientRect();
  const trackTop = trackRect.top;
  const livePinStart = window.scrollY + trackTop - stickyTop;
  const liveRelative = stickyTop - trackTop;
  const lockEnd = getWalkthroughPinTrackScrollPx();
  const frameFixed = track.classList.contains('is-frame-fixed');

  if (
    pinnedSessionStartScrollY === null &&
    liveRelative >= 0 &&
    Math.round(liveRelative) <= lockEnd + 8
  ) {
    pinnedSessionStartScrollY = livePinStart;
  }

  if (liveRelative < -24 || trackRect.bottom <= stickyTop + 64 || walkthroughExitedPastLockEnd) {
    pinnedSessionStartScrollY = null;
  }

  const pinStartScrollY = pinnedSessionStartScrollY ?? livePinStart;
  const relative = frameFixed ? window.scrollY - pinStartScrollY : liveRelative;

  return {
    stickyTop,
    trackTop,
    trackBottom: trackRect.bottom,
    pinStartScrollY,
    relative,
    runway: WALKTHROUGH_PIN_RUNWAY_PX,
  };
}

/** Limit wheel/trackpad speed in the pin runway — phases follow scroll position naturally. */
export function useWalkthroughPinScrollLock(
  trackRef: RefObject<HTMLElement | null>,
  { disabled = false }: { disabled?: boolean } = {}
): void {
  useEffect(() => {
    if (disabled || typeof window === 'undefined') return;

    let lastScrollY = window.scrollY;
    let skipOnce = false;
    let snapDisabled = false;

    const setSnapDisabled = (disable: boolean) => {
      if (disable === snapDisabled) return;
      snapDisabled = disable;
      if (disable) {
        document.documentElement.classList.add('walkthrough-locking');
      } else {
        document.documentElement.classList.remove('walkthrough-locking');
      }
    };

    const onScroll = () => {
      const scrollY = window.scrollY;

      if (skipOnce) {
        skipOnce = false;
        lastScrollY = scrollY;
        return;
      }

      if (pinScrollLockSuspended) {
        lastScrollY = scrollY;
        return;
      }

      const track = trackRef.current;
      if (!track) {
        setSnapDisabled(false);
        lastScrollY = scrollY;
        return;
      }

      const metrics = readPinMetrics(track);
      if (!metrics) {
        setSnapDisabled(false);
        lastScrollY = scrollY;
        return;
      }

      const { relative, pinStartScrollY, trackBottom, stickyTop } = metrics;
      const lockEnd = getWalkthroughPinTrackScrollPx();
      const inLockZone = isWalkthroughPinEngaged(
        { top: metrics.trackTop, bottom: trackBottom },
        relative,
        stickyTop
      );

      // Disable page scroll-snap from 80px before the pin zone through 500px past the
      // section bottom — prevents any snap point from yanking the page backward on Day 3 exit.
      setSnapDisabled(relative >= -80 && trackBottom > -500);

      const delta = scrollY - lastScrollY;
      const roundedRelative = Math.round(relative);

      const inDayRunway = roundedRelative >= 0 && roundedRelative <= WALKTHROUGH_PIN_RUNWAY_PX;

      if (inLockZone && inDayRunway && Math.abs(delta) > WALKTHROUGH_RUNWAY_CAP_PX) {
        const clamped = capWalkthroughScrollY(lastScrollY, scrollY, pinStartScrollY, lockEnd);
        skipOnce = true;
        window.scrollTo({ top: clamped, behavior: 'auto' });
        lastScrollY = clamped;
        return;
      }

      lastScrollY = scrollY;
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      setSnapDisabled(false);
    };
  }, [disabled, trackRef]);
}

/** Fixed frame + spacer while engaged — keeps the card locked without a full-viewport veil. */
export function useWalkthroughFixedFramePin(
  trackRef: RefObject<HTMLElement | null>,
  frameRef: RefObject<HTMLElement | null>,
  spacerRef: RefObject<HTMLElement | null>,
  { disabled = false }: { disabled?: boolean } = {}
): void {
  useEffect(() => {
    let frameWasFixed = false;
    let naturalFrameHeight = 0;

    const reset = () => {
      const t = trackRef.current;
      const f = frameRef.current;
      const s = spacerRef.current;
      t?.classList.remove('is-frame-fixed');
      if (f) {
        f.classList.remove('is-frame-fixed', 'is-frame-locking');
        f.style.removeProperty('position');
        f.style.removeProperty('top');
        f.style.removeProperty('left');
        f.style.removeProperty('width');
        f.style.removeProperty('z-index');
      }
      if (s) s.style.removeProperty('height');
      if (t) restoreWalkthroughTrackHeight(t);
      frameWasFixed = false;
      naturalFrameHeight = 0;
    };

    const track = trackRef.current;
    const frame = frameRef.current;
    const spacer = spacerRef.current;

    if (disabled || !track || !frame || !spacer) {
      reset();
      return;
    }

    const apply = () => {
      const t = trackRef.current;
      const f = frameRef.current;
      const s = spacerRef.current;
      if (!t || !f || !s) return;

      const metrics = readPinMetrics(t);
      if (!metrics) return;

      if (
        !frameWasFixed &&
        f.style.position === 'absolute' &&
        (metrics.relative < 0 || metrics.trackBottom <= 0)
      ) {
        reset();
        return;
      }

      const engaged = isWalkthroughPinEngaged(
        { top: metrics.trackTop, bottom: metrics.trackBottom },
        metrics.relative,
        metrics.stickyTop
      );
      const lockEnd = getWalkthroughPinTrackScrollPx();
      const replayingBackwardAfterExit =
        walkthroughExitedPastLockEnd &&
        metrics.relative >= 0 &&
        metrics.relative <= lockEnd &&
        metrics.trackBottom > metrics.stickyTop;

      if (engaged || replayingBackwardAfterExit) {
        const justLocked = !frameWasFixed;
        if (justLocked) {
          naturalFrameHeight = Math.ceil(f.getBoundingClientRect().height);
          if (naturalFrameHeight > 0) {
            cacheWalkthroughLayout(naturalFrameHeight);
            restoreWalkthroughTrackHeight(t);
          }
        }
        const frameH = naturalFrameHeight || Math.ceil(f.getBoundingClientRect().height);
        const rect = t.getBoundingClientRect();
        frameWasFixed = true;
        s.style.height = `${frameH}px`;
        t.classList.add('is-frame-fixed');
        f.classList.add('is-frame-fixed');
        f.style.position = 'fixed';
        f.style.top = `${metrics.stickyTop}px`;
        f.style.left = `${rect.left}px`;
        f.style.width = `${rect.width}px`;
        f.style.zIndex = '8';
        return;
      }

      if (frameWasFixed) {
        const frameH = naturalFrameHeight || Math.ceil(f.getBoundingClientRect().height);
        const shouldHandOffToAbsolute =
          walkthroughExitedPastLockEnd && metrics.relative >= lockEnd && metrics.trackBottom > 0;
        if (shouldHandOffToAbsolute) {
          const topWithinTrack = Math.max(
            0,
            Math.round(metrics.trackBottom - metrics.trackTop - frameH)
          );
          t.classList.remove('is-frame-fixed');
          f.classList.remove('is-frame-fixed', 'is-frame-locking');
          f.style.position = 'absolute';
          f.style.top = `${topWithinTrack}px`;
          f.style.left = '0';
          f.style.width = '100%';
          f.style.zIndex = '8';
          s.style.removeProperty('height');
          restoreWalkthroughTrackHeight(t);
          frameWasFixed = false;
          naturalFrameHeight = 0;
        } else {
          reset();
        }
      }
    };

    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(frame);
    window.addEventListener('scroll', apply, { passive: true });
    window.addEventListener('resize', apply, { passive: true });

    return () => {
      ro.disconnect();
      window.removeEventListener('scroll', apply);
      window.removeEventListener('resize', apply);
      reset();
    };
  }, [disabled, trackRef, frameRef, spacerRef]);
}

/** Size the pin track to content + runway so sticky does not leave a huge void. */
export function useWalkthroughPinTrackHeight(
  trackRef: RefObject<HTMLElement | null>,
  frameRef: RefObject<HTMLElement | null>,
  { disabled = false }: { disabled?: boolean } = {}
): void {
  useEffect(() => {
    if (disabled) return;

    const track = trackRef.current;
    const frame = frameRef.current;
    if (!track || !frame) return;

    const apply = () => {
      const t = trackRef.current;
      const f = frameRef.current;
      if (!t || !f) return;
      if (f.classList.contains('is-frame-fixed')) return;
      const frameH = measureWalkthroughFrameHeight(f, t);
      const trackH = getWalkthroughPinTrackHeightPx(frameH);
      t.style.height = `${trackH}px`;
    };

    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(frame);
    window.addEventListener('resize', apply, { passive: true });

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', apply);
      if (trackRef.current) trackRef.current.style.removeProperty('height');
    };
  }, [disabled, trackRef, frameRef]);
}
